<script setup>
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { differenceInDays } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAccountFeatures } from 'dashboard/composables/useAccountFeatures';
import UsagesAPI from 'dashboard/api/usages';
import BarChart from 'shared/components/charts/BarChart.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import DateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import BaseSettingsHeader from '../../settings/components/BaseSettingsHeader.vue';
import BuySeatModal from 'dashboard/components-next/BuySeatModal.vue';
import BuyTokenModal from 'dashboard/components-next/BuyTokenModal.vue';
import BuyStorageModal from 'dashboard/components-next/BuyStorageModal.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const { hasMarketingCloudTenantId } = useAccountFeatures();

const buySeatModalRef = ref(null);
const buyTokenModalRef = ref(null);
const buyStorageModalRef = ref(null);
const contactManagerDialogRef = ref(null);
const isLoading = ref(true);
const usageData = ref({ agents: {}, pilot_tokens: {}, storage: {} });
const chartData = ref(null);
const chartGranularity = ref('hourly');
const selectedDays = ref(7);
const isCustomMode = ref(false);
const customDateRange = ref([]);

const MAX_CUSTOM_DAYS = 60;

const seatTooltipLeft = ref(0);
const tokenTooltipLeft = ref(0);
const storageTooltipLeft = ref(0);

const onSeatMouseMove = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  seatTooltipLeft.value = e.clientX - rect.left;
};

const onTokenMouseMove = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  tokenTooltipLeft.value = e.clientX - rect.left;
};

const onStorageMouseMove = e => {
  const rect = e.currentTarget.getBoundingClientRect();
  storageTooltipLeft.value = e.clientX - rect.left;
};

const dayTabs = computed(() => [
  { label: t('SUBSCRIPTION.USAGE.CHART.TODAY'), value: 1 },
  { label: t('SUBSCRIPTION.USAGE.CHART.DAYS_7'), value: 7 },
  { label: t('SUBSCRIPTION.USAGE.CHART.DAYS_30'), value: 30 },
]);

const activeDayTabIndex = computed(() => {
  if (isCustomMode.value) return -1;
  return dayTabs.value.findIndex(tab => tab.value === selectedDays.value);
});

const handleDayTabChanged = tab => {
  isCustomMode.value = false;
  selectedDays.value = tab.value;
  fetchTokenChart();
};

const handleCustomDateChange = range => {
  if (!range || range.length !== 2) return;
  const [start, end] = range;
  const diff = differenceInDays(end, start);
  if (diff > MAX_CUSTOM_DAYS) {
    useAlert(t('SUBSCRIPTION.USAGE.CHART.MAX_DAYS_ERROR', { max: MAX_CUSTOM_DAYS }));
    return;
  }
  customDateRange.value = range;
  isCustomMode.value = true;
  fetchTokenChart();
};

const handleBuySeat = () => {
  if (hasMarketingCloudTenantId.value) {
    contactManagerDialogRef.value?.open();
    return;
  }
  buySeatModalRef?.value?.open();
};

const handleBuyToken = () => {
  if (hasMarketingCloudTenantId.value) {
    contactManagerDialogRef.value?.open();
    return;
  }
  buyTokenModalRef?.value?.open();
};

const handleBuyStorage = () => {
  if (hasMarketingCloudTenantId.value) {
    contactManagerDialogRef.value?.open();
    return;
  }
  buyStorageModalRef?.value?.open();
};

const fetchUsage = async () => {
  isLoading.value = true;
  try {
    const response = await UsagesAPI.getUsage();
    usageData.value = response.data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch usage:', error);
  } finally {
    isLoading.value = false;
  }
};

const formatHourLabel = date => `${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:00`;
const formatDayLabel = date => `${date.getMonth() + 1}/${date.getDate()}`;

const fetchTokenChart = async () => {
  try {
    let response;
    let days;
    let startOfRange;
    let endOfRange;

    const formatLocalDate = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    if (isCustomMode.value && customDateRange.value.length === 2) {
      const [start, end] = customDateRange.value;
      response = await UsagesAPI.getTokenChart({
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      });
      days = differenceInDays(end, start) + 1;
      startOfRange = new Date(start);
      startOfRange.setHours(0, 0, 0, 0);
      endOfRange = new Date(end);
      endOfRange.setHours(23, 59, 59, 999);
    } else {
      response = await UsagesAPI.getTokenChart({ days: selectedDays.value });
      days = selectedDays.value;
      endOfRange = new Date();
      startOfRange = new Date(endOfRange);
      startOfRange.setHours(0, 0, 0, 0);
      if (days > 1) {
        startOfRange.setDate(startOfRange.getDate() - days + 1);
      }
    }

    const rawData = response.data.data || {};
    const granularity = response.data.granularity || 'hourly';
    chartGranularity.value = granularity;

    const allSlots = [];
    const stepMs = granularity === 'hourly' ? 3600 * 1000 : 86400 * 1000;

    for (let t = startOfRange.getTime(); t <= endOfRange.getTime(); t += stepMs) {
      allSlots.push(new Date(t));
    }

    const formatKey = d => {
      if (granularity === 'daily') {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours()).toISOString();
    };

    const dataMap = {};
    Object.entries(rawData).forEach(([key, value]) => {
      const d = new Date(key);
      dataMap[formatKey(d)] = value;
    });

    const labels = allSlots.map(d => granularity === 'daily' ? formatDayLabel(d) : formatHourLabel(d));
    const values = allSlots.map(d => dataMap[formatKey(d)] || 0);

    chartData.value = {
      labels,
      datasets: [
        {
          label: t('SUBSCRIPTION.USAGE.CHART.LABEL'),
          data: values,
          backgroundColor: 'rgba(139, 92, 246, 0.6)',
          borderColor: 'rgba(139, 92, 246, 1)',
          borderWidth: 0,
          borderRadius: 2,
          barPercentage: 0.8,
        },
      ],
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch token chart:', error);
  }
};

const crosshairPlugin = {
  id: 'crosshair',
  afterDraw(chart) {
    const active = chart.tooltip?.getActiveElements();
    if (!active || active.length === 0) return;

    const { ctx } = chart;
    const x = active[0].element.x;
    const topY = chart.scales.y.top;
    const bottomY = chart.scales.y.bottom;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, topY);
    ctx.lineTo(x, bottomY);
    ctx.lineWidth = chart.chartArea.width / chart.data.labels.length;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
    ctx.fillRect(x - ctx.lineWidth / 2, topY, ctx.lineWidth, bottomY - topY);
    ctx.restore();
  },
};

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      bodyFont: { size: 16 },
      callbacks: {
        label: context => `${context.raw.toLocaleString()} Tokens`,
      },
    },
    crosshair: true,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        maxRotation: 0,
        maxTicksLimit: 12,
        font: { size: 11 },
        callback(value) {
          return this.getLabelForValue(value);
        },
      },
    },
    y: {
      grid: { color: '#f1f5f9' },
      border: { display: false },
      ticks: {
        font: { size: 11 },
        callback(value) {
          if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
          return value;
        },
      },
    },
  },
}));

const chartTotalTokens = computed(() => {
  if (!chartData.value) return 0;
  return chartData.value.datasets[0].data.reduce((sum, v) => sum + v, 0);
});

const agentsPercentage = computed(() => usageData.value.agents?.percentage || 0);
const agentsConsumed = computed(() => usageData.value.agents?.consumed || 0);
const agentsTotal = computed(() => usageData.value.agents?.total || 0);
const agentsRemaining = computed(() => agentsTotal.value - agentsConsumed.value);
const agentsBarColor = computed(() => {
  if (agentsPercentage.value >= 80) return '#ef4444';
  if (agentsPercentage.value >= 50) return '#f59e0b';
  return '#3b82f6';
});

const tokensPercentage = computed(() => usageData.value.pilot_tokens?.percentage || 0);
const tokensConsumed = computed(() => usageData.value.pilot_tokens?.consumed || 0);
const tokensTotal = computed(() => usageData.value.pilot_tokens?.total || 0);
const tokensRemaining = computed(() => tokensTotal.value - tokensConsumed.value);
const tokensBarColor = computed(() => {
  if (tokensPercentage.value >= 80) return '#ef4444';
  if (tokensPercentage.value >= 50) return '#f59e0b';
  return '#8b5cf6';
});

// 存储配额计算属性
const storagePercentage = computed(() => usageData.value.storage?.percentage || 0);
const storageConsumed = computed(() => usageData.value.storage?.consumed || 0);
const storageTotal = computed(() => usageData.value.storage?.total || 0);
const storageRemaining = computed(() => storageTotal.value - storageConsumed.value);
const storageBarColor = computed(() => {
  if (storagePercentage.value >= 80) return '#ef4444';
  if (storagePercentage.value >= 50) return '#f59e0b';
  return '#10b981';
});

// 字节转 GB
const formatBytesToGB = bytes => {
  if (!bytes || bytes === 0) return '0';
  return (bytes / 1024 / 1024 / 1024).toFixed(2);
};


onMounted(async () => {
  await Promise.all([fetchUsage(), fetchTokenChart()]);
});
</script>

<template>
  <div class="flex flex-col gap-8 flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('SUBSCRIPTION.USAGE.TITLE')"
      :description="$t('SUBSCRIPTION.USAGE.DESCRIPTION')"
      feature-name="subscription"
    />

    <!-- 卡片区域 -->
    <div v-if="!isLoading" class="bg-n-solid-2 rounded-xl border border-n-weak">
      <!-- 第一行：客服坐席 | AI Token -->
      <div class="grid grid-cols-2 divide-x divide-n-weak">
        <!-- 客服坐席额度 -->
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-md bg-blue-50 flex items-center justify-center">
                <i class="text-blue-600 i-lucide-square-user text-sm" />
              </div>
              <span class="text-lg font-medium text-n-slate-11">
                {{ t('SUBSCRIPTION.USAGE.AGENTS.TITLE') }}
              </span>
            </div>
            <!-- 暂时隐藏购买坐席按钮，后续开放时改为 v-if="hasMarketingCloudTenantId" -->
            <button
              v-if="false"
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style="color: #fff; background-color: #1F93FF;"
              @click="handleBuySeat"
            >
              {{ t('SUBSCRIPTION.USAGE.AGENTS.BUY') }}
            </button>
          </div>
          <div class="flex items-baseline gap-1.5 mb-3">
            <span class="font-semibold text-n-slate-12" style="font-size: 32px;">
              {{ agentsPercentage }}%
            </span>
            <span class="text-base text-n-slate-8">
              {{ t('SUBSCRIPTION.USAGE.USED') }}
            </span>
          </div>
          <div class="w-full bg-slate-100 rounded-full h-3 mb-3 relative progress-wrapper" @mousemove="onSeatMouseMove">
            <div
              class="h-3 rounded-full progress-bar"
              :style="{ width: `${Math.min(agentsPercentage, 100)}%`, backgroundColor: agentsBarColor }"
            />
            <div class="progress-tooltip" :style="{ left: `${seatTooltipLeft}px` }">
              <div class="progress-tooltip-title">{{ agentsPercentage }}% {{ t('SUBSCRIPTION.USAGE.USED') }}</div>
              <div class="progress-tooltip-detail">{{ agentsConsumed }} / {{ agentsTotal }}</div>
            </div>
          </div>
          <div class="flex items-center gap-1 text-n-slate-10 text-xs">
            <span class="font-medium text-n-slate-12">{{ agentsConsumed }}</span>
            <span>{{ t('SUBSCRIPTION.USAGE.USED') }}</span>
            <span class="text-n-slate-8">/</span>
            <span>{{ agentsTotal }} {{ t('SUBSCRIPTION.USAGE.TOTAL') }}</span>
            <span class="text-n-slate-8">/</span>
            <span class="font-medium text-n-slate-12">{{ agentsRemaining }}</span>
            <span>{{ t('SUBSCRIPTION.USAGE.REMAINING') }}</span>
          </div>
        </div>

        <!-- Pilot AI Token额度 -->
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-lg bg-purple-50 flex items-center justify-center">
                <i class="text-purple-600 i-lucide-zap text-sm" />
              </div>
              <span class="text-lg font-medium text-n-slate-11">
                {{ t('SUBSCRIPTION.USAGE.PILOT_TOKENS.TITLE') }}
              </span>
            </div>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
              style="color: #fff; background-color: #1F93FF;"
              @click="handleBuyToken"
            >
              {{ t('SUBSCRIPTION.USAGE.PILOT_TOKENS.BUY') }}
            </button>
          </div>
          <div class="flex items-baseline gap-1.5 mb-3">
            <span class="font-semibold text-n-slate-12" style="font-size: 32px;">
              {{ tokensPercentage }}%
            </span>
            <span class="text-base text-n-slate-8">
              {{ t('SUBSCRIPTION.USAGE.USED') }}
            </span>
          </div>
          <div class="w-full bg-slate-100 rounded-full h-3 mb-3 relative progress-wrapper" @mousemove="onTokenMouseMove">
            <div
              class="h-3 rounded-full progress-bar"
              :style="{ width: `${Math.min(tokensPercentage, 100)}%`, backgroundColor: tokensBarColor }"
            />
            <div class="progress-tooltip" :style="{ left: `${tokenTooltipLeft}px` }">
              <div class="progress-tooltip-title">{{ tokensPercentage }}% {{ t('SUBSCRIPTION.USAGE.USED') }}</div>
              <div class="progress-tooltip-detail">{{ tokensConsumed.toLocaleString() }} / {{ tokensTotal.toLocaleString() }}</div>
            </div>
          </div>
          <div class="flex items-center gap-1 text-n-slate-10 text-xs">
            <span class="font-medium text-n-slate-12">{{ tokensConsumed.toLocaleString() }}</span>
            <span>{{ t('SUBSCRIPTION.USAGE.USED') }}</span>
            <span class="text-n-slate-8">/</span>
            <span>{{ tokensTotal.toLocaleString() }} {{ t('SUBSCRIPTION.USAGE.TOTAL') }}</span>
            <span class="text-n-slate-8">/</span>
            <span class="font-medium text-n-slate-12">{{ tokensRemaining.toLocaleString() }}</span>
            <span>{{ t('SUBSCRIPTION.USAGE.REMAINING') }}</span>
          </div>
        </div>
      </div>

      <!-- 第二行：文件存储配额 + 留白 -->
      <div class="grid grid-cols-2 divide-x divide-n-weak border-t border-n-weak">
        <!-- 文件存储配额 -->
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-2">
              <div class="w-6 h-6 rounded-md bg-green-50 flex items-center justify-center">
                <i class="text-green-600 i-lucide-hard-drive text-sm" />
              </div>
              <span class="text-lg font-medium text-n-slate-11">
                {{ t('SUBSCRIPTION.USAGE.STORAGE.TITLE') }}
              </span>
            </div>
            <div class="flex items-center gap-2">
              <router-link
                :to="`/app/accounts/${accountId}/settings/storage-usage`"
                class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors text-n-slate-11 border border-n-weak hover:bg-n-solid-2"
              >
                {{ t('SUBSCRIPTION.USAGE.STORAGE.MANAGE') }}
              </router-link>
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                style="color: #fff; background-color: #1F93FF;"
                @click="handleBuyStorage"
              >
                {{ t('SUBSCRIPTION.USAGE.STORAGE.BUY') }}
              </button>
            </div>
          </div>
          <div class="flex items-baseline gap-1.5 mb-3">
            <span class="font-semibold text-n-slate-12" style="font-size: 32px;">
              {{ storagePercentage }}%
            </span>
            <span class="text-base text-n-slate-8">
              {{ t('SUBSCRIPTION.USAGE.USED') }}
            </span>
          </div>
          <div class="w-full bg-slate-100 rounded-full h-3 mb-3 relative progress-wrapper" @mousemove="onStorageMouseMove">
            <div
              class="h-3 rounded-full progress-bar"
              :style="{ width: `${Math.min(storagePercentage, 100)}%`, backgroundColor: storageBarColor }"
            />
            <div class="progress-tooltip" :style="{ left: `${storageTooltipLeft}px` }">
              <div class="progress-tooltip-title">{{ storagePercentage }}% {{ t('SUBSCRIPTION.USAGE.USED') }}</div>
              <div class="progress-tooltip-detail">{{ formatBytesToGB(storageConsumed) }} GB / {{ formatBytesToGB(storageTotal) }} GB</div>
            </div>
          </div>
          <div class="flex items-center gap-1 text-n-slate-10 text-xs">
            <span class="font-medium text-n-slate-12">{{ formatBytesToGB(storageConsumed) }} GB</span>
            <span>{{ t('SUBSCRIPTION.USAGE.USED') }}</span>
            <span class="text-n-slate-8">/</span>
            <span>{{ formatBytesToGB(storageTotal) }} GB {{ t('SUBSCRIPTION.USAGE.TOTAL') }}</span>
            <span class="text-n-slate-8">/</span>
            <span class="font-medium text-n-slate-12">{{ formatBytesToGB(storageRemaining) }} GB</span>
            <span>{{ t('SUBSCRIPTION.USAGE.REMAINING') }}</span>
          </div>
        </div>

        <!-- 留白占位 -->
        <div class="p-6 flex items-center justify-center text-n-slate-6 text-sm">
          <!-- 预留扩展空间 -->
        </div>
      </div>
    </div>

    <!-- 柱状图区域 -->
    <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-medium text-n-slate-11">
          {{ t('SUBSCRIPTION.USAGE.CHART.TITLE') }}
        </h3>
        <div class="flex items-center gap-3">
          <TabBar
            :tabs="dayTabs"
            :initial-active-tab="activeDayTabIndex"
            @tab-changed="handleDayTabChanged"
          />
          <span class="text-n-slate-8">|</span>
          <DateRangePicker
            class="usage-date-picker"
            :confirm-text="t('SUBSCRIPTION.USAGE.CHART.CONFIRM')"
            :placeholder="t('SUBSCRIPTION.USAGE.CHART.CUSTOM')"
            @change="handleCustomDateChange"
          />
        </div>
      </div>
      <div v-if="chartData" class="mb-4">
        <span class="text-base text-n-slate-8">Tokens 消费</span>
        <div class="font-semibold text-n-slate-12" style="font-size: 32px;">
          {{ chartTotalTokens.toLocaleString() }}
        </div>
      </div>
      <div v-if="chartData" style="height: 300px;">
        <BarChart :collection="chartData" :chart-options="chartOptions" :plugins="[crosshairPlugin]" />
      </div>
    </div>

    <BuySeatModal ref="buySeatModalRef" />
    <BuyTokenModal ref="buyTokenModalRef" />
    <BuyStorageModal ref="buyStorageModalRef" />

    <Dialog
      ref="contactManagerDialogRef"
      type="alert"
      :title="t('SUBSCRIPTION.USAGE.MIP_CONTACT_MANAGER_TITLE')"
      :description="t('SUBSCRIPTION.USAGE.MIP_CONTACT_MANAGER')"
      :show-cancel-button="false"
      :confirm-button-label="t('SUBSCRIPTION.USAGE.MIP_CONTACT_MANAGER_OK')"
      width="md"
      @confirm="contactManagerDialogRef?.close()"
    />
  </div>
</template>

<style>
.usage-date-picker .mx-input {
  height: 32px !important;
  font-size: 14px !important;
  margin-bottom: 0 !important;
}

.progress-wrapper {
  cursor: pointer;
  overflow: visible;
}

.progress-bar {
  transition: filter 0.2s, box-shadow 0.2s, transform 0.2s;
  position: relative;
  overflow: hidden;
  transform-origin: center;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(255,255,255,0.25), transparent);
  border-radius: 999px 999px 0 0;
}

.progress-wrapper:hover .progress-bar {
  filter: brightness(1.15) saturate(1.1);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  transform: scaleY(1.5);
}

.progress-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  pointer-events: none;
  z-index: 10;
}

.progress-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1e293b;
}

.progress-wrapper:hover .progress-tooltip {
  opacity: 1;
  visibility: visible;
}

.progress-tooltip-title {
  font-weight: 500;
}

.progress-tooltip-detail {
  color: #94a3b8;
  font-size: 11px;
}
</style>
